import React, { useState, useEffect } from "react";

/**
 * BOOK LAUNCH CHECKLIST — Temple's personal to-do list
 * URL: /book-launch
 *
 * Checkboxes persist in localStorage so progress survives reloads.
 * Organized by phase so Temple isn't overwhelmed.
 */

const PHASES = [
  {
    id: "now",
    eyebrow: "Phase 1 · Right now",
    title: "Finish the book + get the ISBN",
    subtitle: "What you're actively doing this week. Don't skip ahead — this is the foundation.",
    color: "#25a1c2",
    tasks: [
      {
        id: "edit-manuscript",
        title: "Finish final manuscript edit",
        detail: "Last read-through of the book interior. Check for: typos, the word 'TRY' (forbidden), any identity callouts, any toxic-positivity phrases that snuck in.",
      },
      {
        id: "confirm-llc-address",
        title: "Confirm your LLC's registered address is NOT your home",
        detail: "If it is, set up a virtual mailbox (iPostal1.com, ~$10/mo) and update the address with the Arizona Corporation Commission before filing anything new.",
        external: { label: "iPostal1.com", url: "https://www.ipostal1.com" },
      },
      {
        id: "file-trade-name",
        title: "File Arizona Trade Name: 'Tapped In Press'",
        detail: "Owner = your LLC. Cost: $10. azsos.gov → Business Services → Trade Names. Use the LLC address, not your home.",
        external: { label: "azsos.gov · Trade Names", url: "https://azsos.gov/business/trade-names-trademarks" },
      },
      {
        id: "register-bowker",
        title: "Register Bowker account",
        detail: "myidentifiers.com. Account holder = your LLC (use the EIN, not your SSN). Imprint = Tapped In Press.",
        external: { label: "myidentifiers.com", url: "https://www.myidentifiers.com" },
      },
      {
        id: "buy-isbns",
        title: "Buy the 10-pack of ISBNs ($295)",
        detail: "Skip ALL upsells: barcodes, CopyrightsNow, author listings, marketing packages. Just the 10-pack.",
      },
      {
        id: "assign-isbn-1",
        title: "Assign ISBN #1 to the book",
        detail: "Title = your final title. Author = Temple Franklin. Publisher = Tapped In Press. Format = Trade Paperback. BISAC codes: SEL031000, OCC019000, SEL045000.",
      },
      {
        id: "assign-isbn-2",
        title: "Reserve ISBN #2 for the Validation Journal",
        detail: "Same process. Title = The Validation Journal. Even if not formatted yet — reserves the metadata slot.",
      },
    ],
  },
  {
    id: "lulu",
    eyebrow: "Phase 2 · Once ISBN is in hand",
    title: "Set up Lulu and go live",
    subtitle: "These steps happen the same day or the day after your ISBN drops.",
    color: "#068fa2",
    tasks: [
      {
        id: "update-copyright-page",
        title: "Paste real ISBN into the copyright page",
        detail: "Replace the 978-X-XXXXX-XXX-X placeholder with the real number from Bowker.",
      },
      {
        id: "lulu-account",
        title: "Confirm Lulu account is under your LLC",
        detail: "Account name, billing, tax info all under the LLC's EIN — not your personal SSN.",
        external: { label: "lulu.com", url: "https://www.lulu.com" },
      },
      {
        id: "lulu-isbn",
        title: "On Lulu's ISBN page, pick 'I'll use my own ISBN'",
        detail: "Paste your Bowker ISBN. Do NOT pick Lulu's free ISBN — that makes Lulu the publisher of record forever.",
      },
      {
        id: "upload-interior",
        title: "Upload book interior PDF (5.5 × 8.5)",
        detail: "Cream paper, perfect bound, paperback. Lulu validates the PDF and shows you a preview.",
      },
      {
        id: "design-cover",
        title: "Design or upload book cover",
        detail: "Use Lulu's cover template generator — it gives you the exact spine width based on your page count. Lulu auto-adds the ISBN barcode to the back cover.",
      },
      {
        id: "order-proof",
        title: "Order a physical proof copy ($10–15)",
        detail: "Hold the real book in your hands before going live. Check trim, paper feel, color accuracy, spine, the smell of victory.",
      },
      {
        id: "approve-proof",
        title: "Approve the proof + publish",
        detail: "Once it feels right, click Publish. Lulu distributes to Amazon, Ingram, Barnes & Noble — takes 1–3 weeks to appear on each.",
      },
    ],
  },
  {
    id: "thirty-days",
    eyebrow: "Phase 3 · Within 30 days of launch",
    title: "Lock down the brand and the funnel",
    subtitle: "Don't do these on launch day. Do them in the calm 2-4 weeks after.",
    color: "#d4af6a",
    tasks: [
      {
        id: "amazon-author-central",
        title: "Set up Amazon Author Central page",
        detail: "Use 'Temple Franklin' as the public name. Add author bio, photo, blog feed (your /blog), and link to mindbodyspirithygienetools.com.",
        external: { label: "author.amazon.com", url: "https://author.amazon.com" },
      },
      {
        id: "goodreads-author",
        title: "Claim Goodreads Author profile",
        detail: "Once the book is live on Amazon, claim it on Goodreads. Add bio, photo, run a Giveaway 30 days in for visibility.",
        external: { label: "goodreads.com/author/program", url: "https://www.goodreads.com/author/program" },
      },
      {
        id: "uspto-trademark",
        title: "File USPTO trademarks: 'MIND BODY SPIRIT HYGIENE' + 'TEMPLE FRANKLIN'",
        detail: "TWO separate filings. (1) MIND BODY SPIRIT HYGIENE wordmark — Classes 16 (printed publications) + 41 (educational services / coaching). (2) TEMPLE FRANKLIN as a service mark covering coaching, speaking, books, podcasts. Owner: your LLC. Cost: ~$350 each = ~$700 total. Takes 8-12 months to register but you can use ™ immediately on both.",
        external: { label: "uspto.gov · TEAS", url: "https://www.uspto.gov/trademarks/apply" },
      },
      {
        id: "second-imprint-trade-name",
        title: "(Optional) File Trade Name for 'Mind Body Spirit HYGIENE™' as a second imprint",
        detail: "If you want a second imprint for journals/courses separate from the book imprint. $10. Skip if you're keeping it simple.",
      },
    ],
  },
  {
    id: "promotion",
    eyebrow: "Phase 4 · Promotion engine",
    title: "Drive traffic to the book",
    subtitle: "The book exists. Now people need to find it. Spread these out — don't try to launch everything in week one.",
    color: "#5EEAD4",
    tasks: [
      {
        id: "upload-youtube-banner",
        title: "Upload the new YouTube channel banner",
        detail: "Already designed — download from /downloads/youtube-banner-2048x1152.png. studio.youtube.com → Customization → Branding → upload.",
      },
      {
        id: "launch-email-1",
        title: "Send Email #1 of the 52-week sequence (announcement)",
        detail: "Email your MailerLite list announcing the book is live. Include: cover image, one-paragraph why, Amazon link, free chapter download.",
      },
      {
        id: "draft-emails-2-5",
        title: "Draft Emails 2–5 of the 52-week sequence",
        detail: "These deepen the relationship with new subscribers right after launch.",
      },
      {
        id: "blog-post-launch",
        title: "Write the launch blog post",
        detail: "On /blog. Tell the origin story: the kitchen trash moment, the 'I GET TO' landing. Make it personal. Link to buy.",
      },
      {
        id: "card-pull-tie-in",
        title: "Film a YouTube card pull tied to a chapter",
        detail: "Pick one card that matches a theme from the book. Do a pull. End with: 'this lives deeper in chapter X of the book — link below.'",
      },
      {
        id: "podcast-pitches",
        title: "Pitch 10 podcasts that interview self-help authors",
        detail: "Examples: We Can Do Hard Things, Almost 30, The Goop Podcast, On Purpose with Jay Shetty, Conversations with Liv. Send a personal pitch with the book + bio + 3 talking points.",
      },
      {
        id: "boundary-course-landing",
        title: "Build 'The Buck Stops Here' $97 course landing page",
        detail: "Upsell from the book. Use Stripe Payment Link.",
      },
    ],
  },
];

const STORAGE_KEY = "book_launch_checklist_v1";

const BookLaunchChecklist = () => {
  const [completed, setCompleted] = useState({});
  const [openPhase, setOpenPhase] = useState("now");

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setCompleted(JSON.parse(saved));
    } catch (e) {
      // ignore
    }
    document.title = "Book Launch Checklist · Temple Franklin";
  }, []);

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
    } catch (e) {
      // ignore
    }
  }, [completed]);

  const toggle = (taskId) => {
    setCompleted((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const phaseProgress = (phase) => {
    const done = phase.tasks.filter((t) => completed[t.id]).length;
    return { done, total: phase.tasks.length };
  };

  const totalDone = PHASES.reduce(
    (acc, p) => acc + p.tasks.filter((t) => completed[t.id]).length,
    0
  );
  const totalTasks = PHASES.reduce((acc, p) => acc + p.tasks.length, 0);
  const overallPct = Math.round((totalDone / totalTasks) * 100);

  return (
    <div className="min-h-screen bg-[#efeae7]" data-testid="book-launch-page">
      {/* HEADER */}
      <header className="pt-16 pb-12 px-6 md:pt-24 bg-[#042F2E] text-white">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase text-[#d4af6a] font-medium mb-5"
            data-testid="checklist-eyebrow"
          >
            Temple's private workspace
          </p>
          <h1
            className="font-serif text-4xl md:text-6xl tracking-tighter leading-[1.0] mb-6"
            data-testid="checklist-headline"
          >
            Book launch <span className="italic text-[#5EEAD4]">checklist.</span>
          </h1>
          <p
            className="font-serif italic text-xl text-white/80 leading-relaxed mb-8 max-w-xl"
            data-testid="checklist-sub"
          >
            One thing at a time. Top to bottom. Your progress saves automatically.
          </p>

          {/* OVERALL PROGRESS BAR */}
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-sans tracking-wide text-[#5EEAD4]" data-testid="overall-progress-label">
              {totalDone} of {totalTasks} complete
            </span>
            <span className="font-sans font-semibold text-white" data-testid="overall-progress-pct">
              {overallPct}%
            </span>
          </div>
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#5EEAD4] to-[#d4af6a] transition-all duration-500"
              style={{ width: `${overallPct}%` }}
              data-testid="overall-progress-bar"
            />
          </div>
        </div>
      </header>

      {/* PHASES */}
      <main className="px-6 pb-24 -mt-6">
        <div className="max-w-3xl mx-auto space-y-5">
          {PHASES.map((phase) => {
            const { done, total } = phaseProgress(phase);
            const isOpen = openPhase === phase.id;
            const phaseComplete = done === total;

            return (
              <section
                key={phase.id}
                className="bg-white rounded-3xl shadow-sm border border-[#CCFBF1] overflow-hidden"
                data-testid={`phase-${phase.id}`}
              >
                {/* PHASE HEADER */}
                <button
                  type="button"
                  onClick={() => setOpenPhase(isOpen ? null : phase.id)}
                  className="w-full text-left px-6 py-6 md:px-10 md:py-8 flex items-center gap-5 hover:bg-[#f7f4f1] transition-colors"
                  data-testid={`phase-toggle-${phase.id}`}
                >
                  {/* Number circle */}
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center font-serif text-2xl flex-shrink-0"
                    style={{
                      background: phaseComplete ? phase.color : `${phase.color}22`,
                      color: phaseComplete ? "white" : phase.color,
                      border: `1.5px solid ${phase.color}`,
                    }}
                  >
                    {phaseComplete ? "✓" : `${done}/${total}`}
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-1"
                      style={{ color: phase.color }}
                    >
                      {phase.eyebrow}
                    </p>
                    <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#042F2E] leading-tight">
                      {phase.title}
                    </h2>
                    <p className="font-sans text-sm text-[#475569] mt-2 hidden md:block">
                      {phase.subtitle}
                    </p>
                  </div>

                  {/* Chevron */}
                  <div
                    className="text-2xl text-[#475569] transition-transform"
                    style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                  >
                    ›
                  </div>
                </button>

                {/* PHASE TASKS */}
                {isOpen && (
                  <div className="px-6 pb-8 md:px-10 md:pb-10 space-y-3" data-testid={`phase-body-${phase.id}`}>
                    <p className="font-sans text-sm text-[#475569] md:hidden mb-4">{phase.subtitle}</p>

                    {phase.tasks.map((task, idx) => {
                      const isDone = !!completed[task.id];
                      return (
                        <div
                          key={task.id}
                          className="flex items-start gap-4 p-4 rounded-2xl transition-colors"
                          style={{
                            background: isDone ? "#f0f9f7" : "#fafafa",
                            borderLeft: `3px solid ${isDone ? phase.color : "#e5e5e5"}`,
                          }}
                          data-testid={`task-${task.id}`}
                        >
                          {/* CHECKBOX */}
                          <button
                            type="button"
                            onClick={() => toggle(task.id)}
                            className="flex-shrink-0 w-7 h-7 rounded-md border-2 transition-all flex items-center justify-center"
                            style={{
                              borderColor: isDone ? phase.color : "#cbd5d1",
                              background: isDone ? phase.color : "white",
                            }}
                            aria-label={isDone ? "Mark incomplete" : "Mark complete"}
                            data-testid={`task-checkbox-${task.id}`}
                          >
                            {isDone && <span className="text-white text-base font-bold">✓</span>}
                          </button>

                          {/* CONTENT */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-1">
                              <span
                                className="font-sans text-[10px] tracking-widest text-[#475569] font-semibold"
                                aria-hidden="true"
                              >
                                {String(idx + 1).padStart(2, "0")}
                              </span>
                              <h3
                                className="font-serif text-lg md:text-xl text-[#042F2E] leading-snug"
                                style={{
                                  textDecoration: isDone ? "line-through" : "none",
                                  opacity: isDone ? 0.55 : 1,
                                }}
                                data-testid={`task-title-${task.id}`}
                              >
                                {task.title}
                              </h3>
                            </div>
                            <p
                              className="font-sans text-sm text-[#475569] leading-relaxed"
                              style={{ opacity: isDone ? 0.6 : 1 }}
                            >
                              {task.detail}
                            </p>
                            {task.external && (
                              <a
                                href={task.external.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-2 font-sans text-xs tracking-wide font-semibold text-[#068fa2] hover:text-[#042F2E] underline-offset-4 hover:underline"
                                data-testid={`task-link-${task.id}`}
                              >
                                → {task.external.label}
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* FOOTER NOTE */}
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <p className="font-serif italic text-[#068fa2] leading-relaxed">
            Bookmark this page. Your checks save in this browser only —
            <br className="hidden md:block" />
            on this device. Same device next time = same progress.
          </p>
          <p className="font-serif text-sm text-[#475569] mt-3">— Locked workspace · not in nav · only you have it</p>
        </div>
      </main>
    </div>
  );
};

export default BookLaunchChecklist;
